/**
 * DraftTab — the Inspector's primary surface with Markdown rendering.
 *
 * Renders one section's drafted text as Markdown while preserving per-sentence
 * citation "why?" reveals. Inline formatting (bold/italic) is preserved across
 * sentence boundaries — a sentence never loses its styling.
 *
 * Data sources (all existing — zero new HTTP):
 *   - getSectionContent(runId, moduleId) — returns draft sections
 *     + provenance.citations for that module
 *   - useCitations — for the per-sentence "why?" reveal
 *
 * Sentence grouping (Phase 4):
 *   The paragraph renderer uses `groupSentences` to slice flowing prose into
 *   sentences while keeping inline elements (<strong>, <em>, etc.) attached to
 *   their sentence. The old `splitSentences` regex returned plain strings only,
 *   losing all formatting. Now: "See **bold**. Next." → two sentence groups,
 *   each preserving its own inline <strong> tags.
 *
 * Component stability (CRITICAL for "why?" toggle):
 *   The markdown component overrides (p, h2, h3, ...) are DEFINED OUTSIDE render
 *   and passed as stable references. react-markdown compares component identity;
 *   if components change every render, it remounts the entire subtree, destroying
 *   the button you clicked. The old code rebuilt components inside render, so
 *   clicking "why?" → setState → re-render → new components object → remount.
 *   Now: stable object → only the clicked sentence re-renders → button stays alive.
 *
 * SRP: this component owns "render one section's Markdown draft + per-sentence
 * citations". Selection-to-citations is delegated to useCitations.
 */

import { Spinner, makeStyles } from "@fluentui/react-components";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { type Citation, getSectionContent } from "../../../api/client";
import { remarkSubSup } from "../../../services/remarkSubSup";
import { remarkSoftBreaks } from "../../../services/remarkSoftBreaks";
import { decodeEscapedWhitespace, normalizeTableSpacing } from "../../../services/markdownNormalize";
import { useCitations } from "../../../hooks/useCitations";
import type { SpineSection } from "../../hooks/useVariantCRun";
import { sanitizeDraftForWord } from "../../services/draftSanitizer";
import { hasTitleFields } from "../../services/titleFieldsView";
import type { RunIdentity } from "../../sseAdapter";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../../tokens";
import TitleFieldsPreview from "./TitleFieldsPreview";

interface DraftTabProps {
  section: SpineSection;
  runId: string;
  docCode: string;
  /** (trial, session) captured from the run_metadata SSE event by
   *  useVariantCRun. Required by useCitations for the per-sentence
   *  "why?" reveal. Null until the metadata event lands (~500ms
   *  post-start); the reveal degrades to a graceful hint until then. */
  identity: RunIdentity | null;
}

interface SectionPayload {
  sections: Array<{
    section_number: string;
    section_title: string;
    draft_content: string;
  }>;
  /** Structured title-page fields (label→value). A title-page module has no
   *  prose body — its content lives here. Attached at the top level of the
   *  section response by the backend; rendered via TitleFieldsPreview. */
  title_fields?: Record<string, string>;
  provenance?: {
    citations?: Array<Record<string, unknown>>;
  };
}

// ---------------------------------------------------------------------------
// Sentence grouping helpers — preserve inline formatting across splits
// ---------------------------------------------------------------------------

/** Sentence boundary: terminal punctuation + lookahead for sentence start. */
const SENTENCE_BOUNDARY = /([.!?])(\s+)(?=[A-Z(0-9"'[])/;
const TERMINAL_END = /[.!?]["')\]]?\s*$/;
const STARTS_SENTENCE = /^\s*[A-Z(0-9"'[]/;

/**
 * Split a single text run into chunks. A chunk with `closes: true` ends a
 * sentence; the trailing chunk (if any) continues into the next element.
 */
function splitStringChunks(s: string): Array<{ text: string; closes: boolean }> {
  const out: Array<{ text: string; closes: boolean }> = [];
  const re = new RegExp(SENTENCE_BOUNDARY, "g");
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) {
    const end = m.index + 1;
    out.push({ text: s.slice(last, end), closes: true });
    last = re.lastIndex;
  }
  if (last < s.length) out.push({ text: s.slice(last), closes: false });
  return out;
}

/** Recursively extract plain text from a React node (for citation lookup). */
function nodeToText(node: React.ReactNode): string {
  if (node == null || node === false || node === true) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join("");
  if (React.isValidElement(node)) {
    return nodeToText((node.props as { children?: React.ReactNode }).children);
  }
  return "";
}

/** Remove inline citation references (e.g., [1], [2]) from text. */
function cleanCitationReferences(text: string): string {
  // Remove patterns like [1], [2], [123], etc. at word boundaries
  return text
    .replace(/\s*\[\d+\]\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Get the text of the last node in an array. */
function lastNodeText(nodes: React.ReactNode[]): string {
  return nodes.length ? nodeToText(nodes[nodes.length - 1]) : "";
}

interface SentenceGroup {
  /** Renderable nodes (strings + inline React elements like <strong>). */
  nodes: React.ReactNode[];
  /** Plain text for citation lookup. */
  text: string;
}

/**
 * Group a block's inline children into sentences while preserving inline
 * formatting. Strings are split on sentence boundaries; inline elements
 * (strong/em/code/a) are appended to the current sentence, never dropped.
 *
 * Special case: if a string chunk *starts* a new sentence (e.g., "Next."),
 * it's flushed to a new group immediately if the prior node ends in
 * sentence-terminal punctuation. This prevents `**Overview.** Body…` from
 * merging into one sentence — the bold header becomes its own group.
 */
function groupSentences(children: React.ReactNode): SentenceGroup[] {
  const arr = React.Children.toArray(children);
  const sentences: SentenceGroup[] = [];
  let current: React.ReactNode[] = [];

  const flush = () => {
    if (current.length > 0) {
      const rawText = current.map(nodeToText).join("").trim();
      if (rawText.length > 0) {
        // Clean citation references from the text for the "Why?" button
        const cleanedText = cleanCitationReferences(rawText);
        if (cleanedText.length > 0) {
          sentences.push({ nodes: current, text: cleanedText });
        }
      }
    }
    current = [];
  };

  for (const child of arr) {
    if (typeof child === "string") {
      // Check if we should flush before this string (element boundary case)
      if (current.length > 0 && TERMINAL_END.test(lastNodeText(current)) && STARTS_SENTENCE.test(child)) {
        flush();
      }

      for (const chunk of splitStringChunks(child)) {
        if (chunk.text) current.push(chunk.text);
        if (chunk.closes) flush();
      }
    } else {
      current.push(child);
    }
  }
  flush();
  return sentences;
}

/** Map our Citation shape to a compact chip label. */
function chipLabel(c: Citation): string {
  const doc = c.document_name || "Source";
  const sec = c.section_name ? ` §${c.section_name}` : "";
  return `${doc}${sec}`;
}

// ---------------------------------------------------------------------------
// Block-structure helpers — lists, soft breaks, bold-only "headers"
// ---------------------------------------------------------------------------

/**
 * React context flag: true while rendering inside a <ul>/<ol>. The paragraph
 * renderer reads it to skip the block-level decoration (the section citation
 * strip and the bold-line→<h4> promotion) that only makes sense for top-level
 * prose — inside a list item that machinery shatters the list. List items still
 * get the per-sentence "why?" reveal.
 */
const InListContext = React.createContext(false);

/**
 * A paragraph whose only meaningful child is a single **bold** run is the
 * model's idiom for a sub-heading (e.g. a standalone `**Overview**` line). We
 * promote ONLY that exact shape to <h4>. We inspect the hast node (not the
 * already-rendered React children, whose <strong> is our own component) so the
 * check stays reliable. The old length/punctuation heuristic mis-promoted
 * ordinary short sentences and list items, so real headings looked wrong.
 */
function isBoldOnlyParagraph(node: unknown): boolean {
  const children = (node as { children?: Array<{ type?: string; tagName?: string; value?: string }> })?.children;
  if (!Array.isArray(children)) return false;
  const meaningful = children.filter((c) => !(c.type === "text" && /^\s*$/.test(c.value ?? "")));
  return meaningful.length === 1 && meaningful[0].type === "element" && meaningful[0].tagName === "strong";
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    padding: "12px 14px 24px",
    fontFamily: fonts.body,
  },
  loading: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "20px 0",
    color: inkColors.ink3,
    fontSize: "12px",
  },
  emptyState: {
    padding: "20px 0",
    color: inkColors.ink4,
    fontSize: "11.5px",
    lineHeight: 1.5,
    fontStyle: "italic",
  },
  errorBanner: {
    padding: "8px 10px",
    background: "#FEF2F2",
    border: "1px solid #FCA5A5",
    borderRadius: "3px",
    fontSize: "11px",
    color: "#7F1D1D",
    marginBottom: "10px",
  },
  paragraph: {
    marginBottom: "12px",
    fontSize: "12px",
    color: inkColors.ink,
    lineHeight: 1.7,
  },
  sentence: {
    cursor: "pointer",
    padding: "0 1px",
    borderRadius: "1px",
    transition: "background-color 0.15s ease-out",
  },
  sentenceOpen: {
    background: "#FEF3C7",
  },
  whyTrigger: {
    fontSize: "9.5px",
    fontWeight: 700,
    color: inkColors.ink4,
    fontFamily: fonts.mono,
    cursor: "pointer",
    marginLeft: "4px",
    letterSpacing: "0.3px",
    background: "none",
    border: "none",
    padding: 0,
    transition: "color 0.15s ease-out",
  },
  whyTriggerOpen: {
    color: brandColors.primary,
  },
  whyPanel: {
    marginTop: "6px",
    marginBottom: "8px",
    padding: "10px",
    background: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
    borderLeft: `3px solid ${brandColors.mid}`,
    borderRadius: "3px",
  },
  whyHeader: {
    fontSize: "9.5px",
    fontWeight: 700,
    color: inkColors.ink4,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "5px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  whyHeaderConfHigh: { color: statusColors.cool },
  whyHeaderConfMed: { color: statusColors.warn },
  whyHeaderConfLow: { color: statusColors.danger },
  whyBody: {
    fontSize: "11px",
    color: inkColors.ink2,
    lineHeight: 1.5,
  },
  citeStrip: {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
    marginTop: "6px",
  },
  citeChip: {
    fontSize: "9.5px",
    color: brandColors.primary,
    background: brandColors.light,
    padding: "2px 7px",
    borderRadius: "3px",
    fontFamily: fonts.mono,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  noCites: {
    fontSize: "10.5px",
    color: inkColors.ink4,
    fontStyle: "italic",
  },
  sectionMeta: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "8px",
  },
  h2: {
    fontSize: "14px",
    fontWeight: 700,
    color: inkColors.ink,
    marginTop: "16px",
    marginBottom: "10px",
  },
  h3: {
    fontSize: "13px",
    fontWeight: 700,
    color: inkColors.ink,
    marginTop: "14px",
    marginBottom: "8px",
  },
  h4: {
    fontSize: "12px",
    fontWeight: 700,
    color: inkColors.ink,
    marginTop: "12px",
    marginBottom: "8px",
  },
  ul: {
    display: "block",
    marginTop: 0,
    marginBottom: "12px",
    paddingLeft: "22px",
    fontSize: "12px",
    color: inkColors.ink,
    lineHeight: 1.7,
    listStyleType: "disc",
    listStylePosition: "outside",
  },
  ol: {
    display: "block",
    marginTop: 0,
    marginBottom: "12px",
    paddingLeft: "22px",
    fontSize: "12px",
    color: inkColors.ink,
    lineHeight: 1.7,
    listStyleType: "decimal",
    listStylePosition: "outside",
  },
  li: {
    display: "list-item",
    marginBottom: "4px",
  },
  // Paragraph rendered inside a list item: plain prose (with per-sentence
  // "why?") and no block decoration, so the list marker and spacing stay clean.
  listParagraph: {
    margin: 0,
    fontSize: "12px",
    color: inkColors.ink,
    lineHeight: 1.7,
  },
  table: {
    borderCollapse: "collapse",
    width: "100%",
    marginBottom: "12px",
    fontSize: "12px",
    border: `1px solid ${surfaceColors.line}`,
  },
  th: {
    backgroundColor: surfaceColors.paperDim,
    padding: "6px 8px",
    textAlign: "left",
    fontWeight: 700,
    color: inkColors.ink,
    // Full cell border so the grid shows both horizontal AND vertical rules.
    border: `1px solid ${surfaceColors.line}`,
    verticalAlign: "top",
  },
  td: {
    padding: "6px 8px",
    border: `1px solid ${surfaceColors.line}`,
    color: inkColors.ink,
    verticalAlign: "top",
  },
  strong: {
    fontWeight: 700,
    color: inkColors.ink,
  },
  em: {
    fontStyle: "italic",
    color: inkColors.ink,
  },
  // ~sub~ / ^sup^ markers (remarkSubSup). line-height 0 stops the raised /
  // lowered glyph from stretching the line box (jitter between lines).
  subSup: {
    fontSize: "0.75em",
    lineHeight: 0,
    color: inkColors.ink,
  },
  code: {
    backgroundColor: surfaceColors.paperDim,
    padding: "2px 4px",
    borderRadius: "2px",
    fontFamily: fonts.mono,
    fontSize: "11px",
    color: inkColors.ink2,
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DraftTab: React.FC<DraftTabProps> = ({ section, runId, docCode, identity }) => {
  const styles = useStyles();
  const [payload, setPayload] = useState<SectionPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openSentenceKey, setOpenSentenceKey] = useState<string | null>(null);

  const requestKeyRef = useRef<string>("");
  const blockParagraphCountersRef = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    void section.id;
    setOpenSentenceKey(null);
    blockParagraphCountersRef.current.clear();
  }, [section.id]);

  const hasDraftContent =
    section.status === "drafted" || section.status === "needs_approval" || section.status === "flagged";

  useEffect(() => {
    if (!hasDraftContent) {
      setLoading(false);
      setError(null);
      setPayload(null);
      return;
    }
    let cancelled = false;
    const key = `${runId}:${section.id}`;
    requestKeyRef.current = key;
    setLoading(true);
    setError(null);
    setPayload(null);
    (async () => {
      try {
        const resp = await getSectionContent(runId, section.id);
        if (cancelled || requestKeyRef.current !== key) return;
        setPayload(resp as unknown as SectionPayload);
      } catch (err) {
        if (cancelled || requestKeyRef.current !== key) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled && requestKeyRef.current === key) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runId, section.id, hasDraftContent]);

  const trial = identity?.trial;
  const session = identity?.session;
  const cites = useCitations({ trial, session, docType: docCode });

  const handleAskWhy = useCallback(
    (sentenceKey: string, sentence: string) => {
      setOpenSentenceKey((prev) => (prev === sentenceKey ? null : sentenceKey));
      if (openSentenceKey === sentenceKey) return;
      if (!trial || !session) return;
      cites.fetchCitations(sentence, section.number).catch((err) => {
        console.warn("[DraftTab] fetchCitations failed", err);
      });
    },
    [openSentenceKey, trial, session, cites, section.number],
  );

  const renderWhyPanel = useCallback(
    (sentenceKey: string) => (
      <div key={`${sentenceKey}-panel`} className={styles.whyPanel}>
        <div className={styles.whyHeader}>
          <span>Why this sentence</span>
          {!trial || !session ? (
            <span style={{ color: inkColors.ink4 }}>· run context unavailable</span>
          ) : cites.isLoading ? (
            <span>· loading…</span>
          ) : cites.error ? (
            <span className={styles.whyHeaderConfLow}>· {cites.error}</span>
          ) : (
            <span
              className={
                cites.status === "ok"
                  ? styles.whyHeaderConfHigh
                  : cites.status === "partial"
                    ? styles.whyHeaderConfMed
                    : styles.whyHeaderConfLow
              }
            >
              · {cites.status?.toUpperCase() ?? "—"}
            </span>
          )}
        </div>
        {!cites.isLoading && cites.citations.length > 0 && (
          <div className={styles.citeStrip}>
            {cites.citations.map((c) => (
              <span key={c.transaction_id} className={styles.citeChip}>
                [{chipLabel(c)}]
              </span>
            ))}
          </div>
        )}
        {!cites.isLoading && cites.citations.length === 0 && (
          <div className={styles.whyBody}>{cites.message ?? "No matching sources found for this sentence."}</div>
        )}
      </div>
    ),
    [trial, session, cites, styles],
  );

  const sectionCitations = (payload?.provenance?.citations ?? []) as unknown as Citation[];

  // ---------------------------------------------------------------------------
  // BUILD COMPONENTS OBJECT ONCE, OUTSIDE RENDER
  // ---------------------------------------------------------------------------
  // This is CRITICAL: react-markdown compares component identity. If we rebuild
  // this object every render, react-markdown remounts the subtree, and clicking
  // "why?" destroys the button. By defining it once per DraftTab instance
  // (memoized below), the component identity stays stable, and only the clicked
  // sentence re-renders.

  // HELPER: render sentences with preservation of inline formatting
  const renderSentencesWithWhy = useCallback(
    (children: React.ReactNode, keyBase: string) => {
      const groups = groupSentences(children);
      if (groups.length === 0) return children;

      return groups.map((group, idx) => {
        const sentenceKey = `${keyBase}-${idx}`;
        const isOpen = openSentenceKey === sentenceKey;

        return (
          <React.Fragment key={sentenceKey}>
            <span
              className={`${styles.sentence} ${isOpen ? styles.sentenceOpen : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => handleAskWhy(sentenceKey, group.text)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleAskWhy(sentenceKey, group.text);
                }
              }}
            >
              {group.nodes}
            </span>
            <button
              type="button"
              className={`${styles.whyTrigger} ${isOpen ? styles.whyTriggerOpen : ""}`}
              onClick={() => handleAskWhy(sentenceKey, group.text)}
              aria-expanded={isOpen}
              aria-label={`Show citations for sentence ${idx + 1}`}
            >
              {isOpen ? "close" : "why?"}
            </button>
            {isOpen && renderWhyPanel(sentenceKey)}{" "}
          </React.Fragment>
        );
      });
    },
    [openSentenceKey, handleAskWhy, renderWhyPanel, styles],
  );

  // Memoize the components object so react-markdown never remounts
  const markdownComponents: Components = useMemo(() => {
    return {
      p: ({ node, children, ...props }) => {
        const insideList = React.useContext(InListContext);
        const paragraphIdx = blockParagraphCountersRef.current.get(-1) || 0;
        blockParagraphCountersRef.current.set(-1, paragraphIdx + 1);

        // Inside a list item: plain prose with the per-sentence "why?" reveal,
        // but no block decoration. The section citation strip and bold→<h4>
        // promotion belong to top-level paragraphs only — applying them here
        // shatters the list marker and spacing.
        if (insideList) {
          return <div className={styles.listParagraph}>{renderSentencesWithWhy(children, `li-${paragraphIdx}`)}</div>;
        }

        // A standalone **bold** line is the model's sub-heading idiom → <h4>.
        if (isBoldOnlyParagraph(node)) {
          return (
            <h4 className={styles.h4} {...props}>
              {children}
            </h4>
          );
        }

        // Regular paragraph: split into sentences with formatting preserved
        return (
          <div className={styles.paragraph}>
            {renderSentencesWithWhy(children, `p-${paragraphIdx}`)}

            {/* Section-level citation strip as Phase-3 fallback */}
            {sectionCitations.length > 0 ? (
              <div className={styles.citeStrip}>
                {sectionCitations.slice(0, 8).map((c, ci) => (
                  <span key={`${c.transaction_id ?? ci}`} className={styles.citeChip}>
                    [{chipLabel(c)}]
                  </span>
                ))}
              </div>
            ) : (
              <div className={styles.noCites}>No citations recorded for this section.</div>
            )}
          </div>
        );
      },

      h2: ({ children, ...props }) => (
        <h2 className={styles.h2} {...props}>
          {children}
        </h2>
      ),
      h3: ({ children, ...props }) => (
        <h3 className={styles.h3} {...props}>
          {children}
        </h3>
      ),
      h4: ({ children, ...props }) => (
        <h4 className={styles.h4} {...props}>
          {children}
        </h4>
      ),
      ul: ({ node: _node, children, ...props }) => (
        <InListContext.Provider value={true}>
          <ul className={styles.ul} {...props}>
            {children}
          </ul>
        </InListContext.Provider>
      ),
      ol: ({ node: _node, children, ...props }) => (
        <InListContext.Provider value={true}>
          <ol className={styles.ol} {...props}>
            {children}
          </ol>
        </InListContext.Provider>
      ),
      li: ({ node: _node, children, ...props }) => (
        <li className={styles.li} {...props}>
          {children}
        </li>
      ),
      table: ({ children, ...props }) => (
        <table className={styles.table} {...props}>
          {children}
        </table>
      ),
      th: ({ children, ...props }) => (
        <th className={styles.th} {...props}>
          {children}
        </th>
      ),
      td: ({ children, ...props }) => (
        <td className={styles.td} {...props}>
          {children}
        </td>
      ),
      strong: ({ children, ...props }: any) => (
        <strong className={styles.strong} {...props}>
          {children}
        </strong>
      ),
      b: ({ children, ...props }: any) => (
        <strong className={styles.strong} {...props}>
          {children}
        </strong>
      ),
      em: ({ children, ...props }: any) => (
        <em className={styles.em} {...props}>
          {children}
        </em>
      ),
      i: ({ children, ...props }: any) => (
        <em className={styles.em} {...props}>
          {children}
        </em>
      ),
      code: ({ children, ...props }: any) => (
        <code className={styles.code} {...props}>
          {children}
        </code>
      ),
      sub: ({ children, ...props }: any) => (
        <sub className={styles.subSup} {...props}>
          {children}
        </sub>
      ),
      sup: ({ children, ...props }: any) => (
        <sup className={styles.subSup} {...props}>
          {children}
        </sup>
      ),
    };
  }, [styles, renderSentencesWithWhy, sectionCitations]);

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className={styles.root}>
        <div className={styles.loading}>
          <Spinner size="extra-tiny" />
          <span>Loading draft…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.root}>
        <div className={styles.errorBanner}>Couldn&apos;t load this section&apos;s draft: {error}</div>
      </div>
    );
  }

  if (!hasDraftContent) {
    return (
      <div className={styles.root}>
        <div className={styles.emptyState}>This section hasn&apos;t been drafted yet. Click Generate to draft it.</div>
      </div>
    );
  }

  const sectionsBlocks = payload?.sections ?? [];

  // Title-page modules carry no prose body — their content is a structured
  // ``title_fields`` dict (empty ``draft_content``). Render those fields the way
  // wordWriter.ts::updateTitlePageFields paints them into the live doc, instead
  // of the (empty) markdown map that leaves the panel blank. Prefer this whenever
  // the section has title fields and no real prose body.
  const titleFields = payload?.title_fields;
  const hasProseBody = sectionsBlocks.some((b) => b.draft_content?.trim());
  if (titleFields && hasTitleFields(titleFields) && !hasProseBody) {
    return (
      <div className={styles.root}>
        <div className={styles.sectionMeta}>
          {section.number} · {section.title}
        </div>
        <TitleFieldsPreview fields={titleFields} />
      </div>
    );
  }

  if (sectionsBlocks.length === 0) {
    return (
      <div className={styles.root}>
        <div className={styles.emptyState}>No content available for this section.</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.sectionMeta}>
        {section.number} · {section.title}
      </div>

      {sectionsBlocks.map((block, blockIdx) => {
        blockParagraphCountersRef.current.set(-1, 0);
        const sanitized = normalizeTableSpacing(sanitizeDraftForWord(decodeEscapedWhitespace(block.draft_content)));

        return (
          <div key={`${block.section_number}-${blockIdx}`}>
            {/* singleTilde:false — GFM's default single-tilde strikethrough
                would consume `~2~` before remarkSubSup can see it. */}
            <ReactMarkdown
              remarkPlugins={[[remarkGfm, { singleTilde: false }], remarkSoftBreaks, remarkSubSup]}
              components={markdownComponents}
            >
              {sanitized}
            </ReactMarkdown>
          </div>
        );
      })}
    </div>
  );
};

export default DraftTab;
